<template>
    <span
        data-testid="onboarding-click-gesture"
        class="onboarding-click-gesture"
        :class="`is-${phase}`"
        aria-hidden="true"
    >
        <img
            class="onboarding-click-gesture__ripple"
            :src="rippleUrl"
            alt=""
            draggable="false"
        >
        <img
            class="onboarding-click-gesture__hand"
            :src="handUrl"
            alt=""
            draggable="false"
        >
    </span>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import handUrl from '../assets/onboarding-click-gesture-hand.svg';
import rippleUrl from '../assets/onboarding-click-gesture-ripple.svg';

/** 小手从右向左淡入的时长。 */
const HAND_ENTER_DURATION_MS = 400;
/** 小手按压动作的时长。 */
const HAND_PRESS_DURATION_MS = 200;
/** 点击波纹扩散的时长。 */
const RIPPLE_DURATION_MS = 500;
/** 小手淡出的时长。 */
const HAND_LEAVE_DURATION_MS = 300;
/** 循环播放下一轮前的停顿时长。 */
const LOOP_PAUSE_DURATION_MS = 200;

type GesturePhase = 'entering' | 'pressing' | 'rippling' | 'leaving' | 'complete' | 'static';

interface Props {
    /** 是否持续循环播放点击手势。 */
    loop?: boolean;
    /** 小手单次进出场期间展示的点击次数。 */
    clickCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
    loop: false,
    clickCount: 1,
});

const emit = defineEmits<{
    /** 单次点击手势完整播放结束。 */
    complete: [];
}>();

const phase = ref<GesturePhase>('entering');
const timeoutIds = new Set<number>();

/** 创建可在组件卸载时统一释放的一次性定时任务。 */
function schedule(callback: () => void, delayMs: number): void {
    const timeoutId = window.setTimeout(() => {
        timeoutIds.delete(timeoutId);
        callback();
    }, delayMs);
    timeoutIds.add(timeoutId);
}

/** 判断系统是否要求减少动态效果。 */
function prefersReducedMotion(): boolean {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/** 在小手保持可见期间依次播放按压和波纹。 */
function startClick(clickIndex: number): void {
    phase.value = 'pressing';
    schedule(() => {
        phase.value = 'rippling';
        schedule(() => {
            if (clickIndex + 1 < Math.max(1, props.clickCount)) {
                startClick(clickIndex + 1);
                return;
            }
            phase.value = 'leaving';
            schedule(() => {
                phase.value = 'complete';
                emit('complete');
                if (props.loop) {
                    schedule(startGesture, LOOP_PAUSE_DURATION_MS);
                }
            }, HAND_LEAVE_DURATION_MS);
        }, RIPPLE_DURATION_MS);
    }, HAND_PRESS_DURATION_MS);
}

/** 播放一次进场、指定次数点击和离场手势。 */
function startGesture(): void {
    phase.value = 'entering';
    schedule(() => startClick(0), HAND_ENTER_DURATION_MS);
}

/** 清理组件生命周期内创建的全部定时器。 */
function clearTimers(): void {
    timeoutIds.forEach(timeoutId => window.clearTimeout(timeoutId));
    timeoutIds.clear();
}

onMounted(() => {
    if (prefersReducedMotion()) {
        phase.value = 'static';
        return;
    }
    startGesture();
});

onBeforeUnmount(clearTimers);
</script>

<style scoped lang="scss">
.onboarding-click-gesture {
    position: relative;
    display: block;
    width: 68px;
    height: 65px;
    pointer-events: none;
    transform-origin: 10px 19px;

    &.is-entering {
        animation: onboarding-click-gesture-hand-enter 400ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
    }

    &.is-pressing .onboarding-click-gesture__hand {
        animation: onboarding-click-gesture-hand-press 200ms ease-in-out both;
    }

    &.is-rippling .onboarding-click-gesture__ripple {
        animation: onboarding-click-gesture-ripple 500ms ease-out both;
    }

    &.is-leaving {
        animation: onboarding-click-gesture-hand-leave 300ms ease-in both;
    }

    &.is-complete {
        visibility: hidden;
        opacity: 0;
    }

    &.is-static .onboarding-click-gesture__ripple {
        opacity: 1;
    }
}

.onboarding-click-gesture__hand {
    position: absolute;
    top: 4px;
    left: 5px;
    display: block;
    width: 68px;
    height: 61px;
    user-select: none;
    // 原始素材内的指尖中心，将按压缩放固定在真实点击点上。
    transform-origin: 5px 15px;
}

.onboarding-click-gesture__ripple {
    position: absolute;
    top: -13.5px;
    left: -23px;
    display: block;
    width: 66px;
    height: 65px;
    opacity: 0;
    user-select: none;
}

@keyframes onboarding-click-gesture-hand-enter {
    from {
        opacity: 0;
        transform: translateX(28px);
    }

    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes onboarding-click-gesture-hand-press {
    0%,
    100% {
        transform: scale(1);
    }

    55% {
        transform: scale(0.94);
    }
}

@keyframes onboarding-click-gesture-ripple {
    0% {
        opacity: 0;
        transform: scale(0.72);
    }

    24% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform: scale(1.08);
    }
}

@keyframes onboarding-click-gesture-hand-leave {
    from {
        opacity: 1;
        transform: translateX(0);
    }

    to {
        opacity: 0;
        transform: translateX(-8px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .onboarding-click-gesture,
    .onboarding-click-gesture__hand,
    .onboarding-click-gesture__ripple {
        animation: none !important;
    }
}
</style>
